import { db } from "db/db";
import { Role, RoleForResolver } from "db/definitions";
import { bulkGetPerms } from "./permissionQueries";

/**
 * Adds a bulk list of roles to the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param roles - An array of `RoleForResolver` objects to be added to the database.
 * @returns A promise that resolves when the roles are successfully added.
 */
export async function bulkAddRoles(roles: RoleForResolver[]): Promise<void> {
	await db.role.bulkAdd(roles);
}

/**
 * Retrieves roles by their IDs, filtering out any invalid entries.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids - Array of role IDs to retrieve.
 * @returns A promise resolving to an array of valid roles.
 */
export async function bulkGetRoles(ids: string[]): Promise<RoleForResolver[]> {
	const roles = (await db.role.bulkGet(ids)).filter((role): role is RoleForResolver => !!role);
	return roles;
}

/**
 * Builds a final `Role` object by resolving permissions based on the `permissionIds` in the provided `RoleForResolver`.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param role - A `RoleForResolver` object containing the role information, including permission IDs.
 * @returns A promise that resolves to a `Role` object with the resolved permissions.
 */
export async function buildRole(role: RoleForResolver): Promise<Role> {
	const permissions = await bulkGetPerms(role.permissionIds);
	const finalRole: Role = { ...role, permissions };
	return finalRole;
}

/**
 * Creates a new role in the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param role - The role to be created.
 * @returns A promise that resolves when the role has been added.
 */
export async function createRole(role: RoleForResolver): Promise<void> {
	await db.role.add(role);
}

/**
 * Converts a Role or RoleForResolver object into a RoleForResolver format.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param role - The role to transform.
 * @returns The role formatted as RoleForResolver.
 */
export function createRoleForResolver(role: Role | RoleForResolver): RoleForResolver {
	const roleForResolver: RoleForResolver = {
		id: role.id,
		description: role.description,
		name: role.name,
		permissionIds: role.permissionIds,
		group: role.group,
		readGroups: role.readGroups,
		createdAt: role.createdAt,
		updatedAt: role.updatedAt,
	};
	return roleForResolver;
}

/**
 * Deletes all role docs in the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 */
export async function deleteAllRoleDocs(): Promise<void> {
	await db.role.clear();
}

/**
 * Deletes a role by its ID from the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id - The ID of the role to delete.
 * @returns A promise that resolves when the role is deleted.
 */
export async function deleteRole(id: string): Promise<void> {
	await db.role.delete(id);
}

/**
 * Fetches all roles and their associated permissions, building complete role objects.
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns A promise resolving to an array of roles with permissions.
 */
export async function fetchRolesAndPerms(): Promise<Role[]> {
	const rolesForResolver = await fetchRoles();
	const finalRolePromises: Promise<Role>[] = [];
	for (const role of rolesForResolver) {
		finalRolePromises.push(buildRole(role));
	}
	const finalRoles = await Promise.all(finalRolePromises);
	return finalRoles;
}

/**
 * Fetches all roles from the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns A promise resolving to an array of roles.
 */
export async function fetchRoles(): Promise<RoleForResolver[]> {
	const roles = await db.role.toArray();
	return roles;
}

/**
 * Updates or creates a role in the database.
 * @author Eric Webb <ewebb@factorearth.com>
 * @param role - The role to be added or updated.
 * @returns A promise that resolves when the role is stored.
 */
export async function putRole(role: RoleForResolver): Promise<void> {
	await db.role.put(role);
}
